import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";
import { http } from "../lib/api";

export const searchCompany = (q) => http.get("/public/company-search", { params: { q } }).then((r) => r.data);

export const vatFromSiren = (siren) => {
  if (!/^\d{9}$/.test(siren || "")) return "";
  const key = (12 + 3 * (Number(siren) % 97)) % 97;
  return `FR${String(key).padStart(2, "0")}${siren}`;
};

export const registryFields = (c) => ({ name: c.name, siret: c.siret, siren: c.siren, vat_number: vatFromSiren(c.siren),
  address: c.address, postal_code: c.postal_code, city: c.city });

export const mergeRegistry = (v, c, overwrite) => {
  const f = registryFields(c), out = { ...v };
  Object.entries(f).forEach(([k, val]) => { if (val && (overwrite || !(v[k] || "").toString().trim())) out[k] = val; });
  return out;
};

export const CompanyOption = (c) => (
  <div><div className="font-semibold">{c.name} {!c.active && <span className="text-[10px] font-bold text-rose-600">ÉTABLISSEMENT FERMÉ</span>}</div>
    <div className="text-xs text-slate-500">SIRET {c.siret} · {c.address}, {c.postal_code} {c.city}</div></div>
);

export const SiretRegistryBar = ({ v, setV }) => {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const last = useRef("");
  const siret = (v.siret || "").replace(/\s/g, "");
  useEffect(() => {
    if (siret.length !== 14) { setRes(null); last.current = ""; return; }
    if (last.current === siret) return;
    last.current = siret;
    setLoading(true);
    searchCompany(siret).then((r) => {
      const c = r[0] || null;
      setRes(c || "none");
      if (c) setV((p) => mergeRegistry(p, c, false));
    }).catch(() => setRes("error")).finally(() => setLoading(false));
  }, [siret]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="mt-3 text-xs text-slate-500 flex items-center gap-2" data-testid="registry-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" />Recherche dans l&apos;annuaire des entreprises…</div>;
  if (!res) return <div className="mt-3 text-xs text-slate-500">Saisissez le SIRET (14 chiffres) pour remplir automatiquement la fiche depuis l&apos;annuaire officiel.</div>;
  if (res === "none" || res === "error") return <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="registry-not-found">{res === "none" ? "SIRET introuvable dans l'annuaire des entreprises." : "Annuaire indisponible, réessayez plus tard."}</div>;
  return (
    <div className={`mt-3 text-sm rounded-md px-3 py-2 border flex flex-wrap items-center gap-3 ${res.active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-800"}`} data-testid="registry-result">
      {res.active ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      <span className="flex-1"><b>{res.name}</b> — {res.address}, {res.postal_code} {res.city}
        {!res.active && <b className="ml-2" data-testid="registry-closed-warning">Établissement fermé</b>}</span>
      <button type="button" data-testid="registry-overwrite-btn" onClick={() => setV((p) => mergeRegistry(p, res, true))} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border border-current bg-white/70 hover:bg-white">
        <RefreshCw className="h-3.5 w-3.5" />Mettre à jour depuis l&apos;annuaire
      </button>
    </div>
  );
};

export const isUK = (country) => /royaume|united kingdom|^uk$|^gb$|angleterre|england|[ée]cosse|scotland|galles|wales|irlande du nord|northern ireland|grande-bretagne/i.test((country || "").trim());
export const isFrance = (country) => !country || /^france$/i.test(country.trim());
export const searchUK = (q) => http.get("/public/company-search-uk", { params: { q } }).then((r) => r.data);
const ukFields = (c) => ({ name: c.name, uk_company_number: c.uk_company_number, address: c.address, postal_code: c.postal_code, city: c.city });
export const mergeForeign = (v, f, overwrite) => {
  const out = { ...v };
  Object.entries(f).forEach(([k, val]) => { if (val && (overwrite || !(v[k] || "").toString().trim())) out[k] = val; });
  return out;
};
export const UkOption = (c) => (
  <div><div className="font-semibold">{c.name} {!c.active && <span className="text-[10px] font-bold text-rose-600 uppercase">{c.status_label || "Fermée"}</span>}</div>
    <div className="text-xs text-slate-500">N° {c.uk_company_number} · {c.address}, {c.postal_code} {c.city}</div></div>
);

const ResultBar = ({ ok, testid, children, onOverwrite }) => (
  <div className={`mt-3 text-sm rounded-md px-3 py-2 border flex flex-wrap items-center gap-3 ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-800"}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
    <span className="flex-1">{children}</span>
    {onOverwrite && <button type="button" data-testid={`${testid}-overwrite`} onClick={onOverwrite} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border border-current bg-white/70 hover:bg-white"><RefreshCw className="h-3.5 w-3.5" />Mettre à jour depuis le registre</button>}
  </div>
);
const Msg = ({ testid, tone = "slate", children }) => <div data-testid={testid} className={`mt-3 text-xs ${tone === "amber" ? "text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" : "text-slate-500"}`}>{children}</div>;

const useLookup = (key, valid, fetch, onFound) => {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const last = useRef("");
  useEffect(() => {
    if (!valid) { setRes(null); last.current = ""; return; }
    if (last.current === key) return;
    last.current = key;
    setLoading(true);
    fetch(key).then((r) => { setRes(r); onFound(r); }).catch((e) => setRes({ error: e?.response?.status === 404 ? "none" : (e?.response?.data?.detail || "error") })).finally(() => setLoading(false));
  }, [key, valid]); // eslint-disable-line react-hooks/exhaustive-deps
  return [res, loading];
};

export const UkRegistryBar = ({ v, setV }) => {
  const num = (v.uk_company_number || "").replace(/\s/g, "").toUpperCase();
  const [res, loading] = useLookup(num, /^[A-Z0-9]{6,8}$/.test(num), (n) => http.get("/public/uk-company", { params: { number: n } }).then((r) => r.data), (r) => setV((p) => mergeForeign(p, ukFields(r), false)));
  if (loading) return <Msg testid="uk-registry-loading"><Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1" />Recherche au registre britannique (Companies House)…</Msg>;
  if (!res) return <Msg testid="uk-registry-hint">Tapez le nom ou saisissez le n° d&apos;entreprise (Companies House, 8 caractères) pour vérifier et remplir la fiche.</Msg>;
  if (res.error) return <Msg testid="uk-registry-not-found" tone="amber">{res.error === "none" ? "Entreprise introuvable au registre britannique." : res.error}</Msg>;
  return (
    <ResultBar ok={res.active} testid="uk-registry-result" onOverwrite={() => setV((p) => mergeForeign(p, ukFields(res), true))}>
      <b>{res.name}</b> — {res.address}, {res.postal_code} {res.city} · <b data-testid="uk-registry-status">{res.active ? "Active" : `Fermée (${res.status_label})`}</b>
    </ResultBar>
  );
};

export const VatRegistryBar = ({ v, setV }) => {
  const vat = (v.vat_number || "").replace(/[\s.-]/g, "").toUpperCase();
  const valid = /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI)[0-9A-Z+*]{2,12}$/.test(vat);
  const [res, loading] = useLookup(vat, valid, (x) => http.get("/public/vat-check", { params: { vat: x } }).then((r) => r.data),
    (r) => r.valid && setV((p) => mergeForeign(p, { name: r.name, address: r.address, postal_code: r.postal_code, city: r.city }, false)));
  if (loading) return <Msg testid="vat-registry-loading"><Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1" />Vérification VIES (Commission européenne)…</Msg>;
  if (!res) return <Msg testid="vat-registry-hint">Saisissez le n° de TVA intracommunautaire avec le préfixe du pays (ex. DE123456789) pour le vérifier auprès de VIES.</Msg>;
  if (res.error) return <Msg testid="vat-registry-error" tone="amber">{res.error === "none" ? "N° de TVA introuvable." : res.error}</Msg>;
  return (
    <ResultBar ok={res.valid} testid="vat-registry-result" onOverwrite={res.valid && (res.name || res.address) ? () => setV((p) => mergeForeign(p, { name: res.name, address: res.address, postal_code: res.postal_code, city: res.city }, true)) : null}>
      <b data-testid="vat-registry-status">{res.valid ? "TVA valide" : "TVA invalide"}</b> · {res.vat_number}{res.name && <> — <b>{res.name}</b></>}{res.address && <> — {res.address} {res.postal_code} {res.city}</>}
      {res.valid && !res.name && <span className="text-xs"> (ce pays ne communique pas la raison sociale)</span>}
    </ResultBar>
  );
};

export const isCH = (country) => /suisse|switzerland|schweiz|svizzera|^ch$/i.test((country || "").trim());
export const searchCH = (q) => http.get("/public/company-search-ch", { params: { q } }).then((r) => r.data);
export const chFields = (c) => ({ name: c.name, ch_uid: c.ch_uid, address: c.address, postal_code: c.postal_code, city: c.city });
export const ChOption = (c) => (
  <div><div className="font-semibold">{c.name} {!c.active && <span className="text-[10px] font-bold text-rose-600 uppercase">{c.status_label}</span>}</div>
    <div className="text-xs text-slate-500">IDE {c.ch_uid} · {c.city}</div></div>
);

export const ChRegistryBar = ({ v, setV }) => {
  const uid = (v.ch_uid || "").replace(/\D/g, "");
  const [res, loading] = useLookup(uid, uid.length === 9, (u) => http.get("/public/ch-company", { params: { uid: u } }).then((r) => r.data), (r) => setV((p) => mergeForeign(p, chFields(r), false)));
  if (loading) return <Msg testid="ch-registry-loading"><Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1" />Recherche au registre du commerce suisse (Zefix)…</Msg>;
  if (!res) return <Msg testid="ch-registry-hint">Tapez le nom ou saisissez le n° IDE (CHE-123.456.789) pour vérifier et remplir la fiche depuis le registre du commerce suisse.</Msg>;
  if (res.error) return <Msg testid="ch-registry-not-found" tone="amber">{res.error === "none" ? "Entreprise introuvable au registre du commerce suisse." : res.error}</Msg>;
  return (
    <ResultBar ok={res.active} testid="ch-registry-result" onOverwrite={() => setV((p) => mergeForeign(p, chFields(res), true))}>
      <b>{res.name}</b> — {res.address}, {res.postal_code} {res.city} · <b data-testid="ch-registry-status">{res.status_label}</b>
    </ResultBar>
  );
};

export const TaxRegimeNote = ({ v }) => {
  if (!v.tax_regime) return null;
  return (
    <div data-testid="client-tax-regime" className="mt-3 text-xs rounded px-3 py-2 border border-indigo-200 bg-indigo-50 text-indigo-900">
      <b>{v.tax_regime === "reverse_charge" ? "Autoliquidation appliquée" : "Prestation non soumise à la TVA française (art. 259 CGI)"}</b> — TVA à 0 % et mention légale ajoutée automatiquement (avec traduction) sur les devis et factures de ce client.
    </div>
  );
};
